import React, { memo } from 'react';
import { RichTextBlock } from 'prismic-reactjs';
import { ImageProps } from '@components/image';
import Text from '@components/text';
import getLinkConfig from '@helpers/get-link-config';
import { Wrapper } from '@components/wrapper.styled';
import AnimatedElement from '@components/animated-element';
import { defaultTheme } from '@styles/theme.styled';
import {
  TitleContainer,
  ImageContainer,
  StyledLinkComponent,
  TextContent,
  FeatureWrapper,
  StyledImage,
  StyledSection,
} from './long-content.styled';

export interface LongContentProps {
  image?: ImageProps;
  title?: string;
  subheading?: RichTextBlock[];
  backgroundColor?: keyof typeof defaultTheme.backgroundColors;
  description?: RichTextBlock[];
  imageAlignment?: 'default' | 'left' | 'right';
  link?: {
    uid?: string;
    url?: string;
  };
  linkLabel?: string;
  ariaLabel?: string;
}

const LongContent: React.FC<LongContentProps> = ({
  image,
  title,
  subheading,
  description,
  link,
  linkLabel,
  imageAlignment = 'default',
  ariaLabel,
  backgroundColor = 'white',
}) => {
  const linkConfig = getLinkConfig(link);
  const hasImage = Boolean(image?.gatsbyImageData || image?.url);
  const hasSubheading = Boolean(subheading?.length);
  const hasDescription = Boolean(description?.length);
  const hasLink = Boolean(linkConfig.url);

  return (
    <StyledSection backgroundColor="white">
      <Wrapper>
        <AnimatedElement
          animationType="slideInLeft"
          delay={0.125}
          threshold={0.25}
          rootMargin="50px"
        >
          <FeatureWrapper
            $imageAlignment={imageAlignment}
            $backgroundColor={backgroundColor}
            role="article"
            aria-label={ariaLabel || title}
          >
            {hasImage && (
              <ImageContainer>
                <StyledImage
                  url={image?.url || ''}
                  alt={image?.alt || title || 'Feature image'}
                  gatsbyImageData={image?.gatsbyImageData}
                  loading="lazy"
                  objectFit="contain"
                  objectPosition="0% 0%"
                />

                <TitleContainer>
                  {title && (
                    <Text
                      tag="h2"
                      type="title"
                      stringText={title}
                      fontSize="heading3"
                      margin="none"
                      fontColor="black"
                    />
                  )}
                  {hasSubheading && (
                    <Text
                      type="subheading"
                      richText={subheading}
                      margin="xxs"
                      fontColor="darkPurple"
                      fontSize="large"
                    />
                  )}
                </TitleContainer>
              </ImageContainer>
            )}
            <TextContent>
              {hasDescription && (
                <Text type="description" richText={description} margin="xxs" fontColor="dark" />
              )}
              {hasLink && (
                <StyledLinkComponent
                  displayArrow={false}
                  type="button"
                  isExternal={linkConfig.isExternal}
                  url={linkConfig.url}
                  title={linkLabel || 'Explore all products and features'}
                  aria-label={linkLabel || 'Explore all products and features'}
                />
              )}
            </TextContent>
          </FeatureWrapper>
        </AnimatedElement>
      </Wrapper>
    </StyledSection>
  );
};

export default memo(LongContent);
